import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Row, Col, DatePicker, Space, Typography, Tag, Statistic, Segmented, message, Alert } from 'antd';
import { DollarOutlined, CheckCircleOutlined, ClockCircleOutlined, ExperimentOutlined } from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip, Legend } from 'recharts';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import api from '../services/api';

dayjs.locale('pt-br');
const { Title, Text } = Typography;

const COLORS = {
  PAID: '#52c41a',       // verde (confirmado)
  RECURRING: '#1890ff',  // azul (previsto recorrente)
  TRIAL: '#faad14',      // amarelo (potencial de trial)
};

const KIND_LABEL = {
  PAID: 'Pago',
  RECURRING: 'Recorrente',
  TRIAL: 'Trial → 1º pagto',
};

const KIND_TAG_COLOR = {
  PAID: 'green',
  RECURRING: 'blue',
  TRIAL: 'orange',
};

const Payments = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(dayjs());
  const [filter, setFilter] = useState('Todos'); // Todos | Pagos | Estimados

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/monitoring/payments', { params: { month: month.format('YYYY-MM') } });
      setData(r.data);
    } catch (err) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const filteredItems = useMemo(() => {
    const items = data?.items || [];
    if (filter === 'Pagos') return items.filter(x => x.kind === 'PAID');
    if (filter === 'Estimados') return items.filter(x => x.kind !== 'PAID');
    return items;
  }, [data, filter]);

  const filteredTotal = useMemo(
    () => filteredItems.reduce((s, r) => s + Number(r.amount || 0), 0),
    [filteredItems]
  );

  const pieData = useMemo(() => {
    const s = data?.summary || {};
    return [
      { name: 'Pagos', value: s.paid?.amount || 0, key: 'PAID' },
      { name: 'Recorrentes', value: s.recurring?.amount || 0, key: 'RECURRING' },
      { name: 'Trials', value: s.trial?.amount || 0, key: 'TRIAL' },
    ].filter(x => x.value > 0);
  }, [data]);

  const columns = [
    { title: 'Tipo', dataIndex: 'kind', width: 130,
      filters: [
        { text: 'Pago', value: 'PAID' },
        { text: 'Recorrente', value: 'RECURRING' },
        { text: 'Trial → 1º pagto', value: 'TRIAL' },
      ],
      onFilter: (v, r) => r.kind === v,
      render: v => <Tag color={KIND_TAG_COLOR[v]}>{KIND_LABEL[v]}</Tag>,
    },
    { title: 'Data', dataIndex: 'date', width: 130,
      render: v => <Text style={{ fontSize: 12 }}>{v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—'}</Text>,
      sorter: (a, b) => new Date(a.date || 0) - new Date(b.date || 0),
      defaultSortOrder: 'descend',
    },
    { title: 'Time', dataIndex: 'game_name', width: 220,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <a href={r.admin_link} target="_blank" rel="noreferrer">{v || '(sem nome)'}</a>
          <Text type="secondary" style={{ fontSize: 11 }}>id {r.customer_id} · {r.cellphone}</Text>
        </Space>
      ),
    },
    { title: 'Plano', dataIndex: 'plan_name', width: 130,
      render: v => <Tag>{v || '?'}</Tag>,
    },
    { title: 'Preço', dataIndex: 'plan_price', width: 90, align: 'right',
      render: v => <Text style={{ fontSize: 12 }}>R$ {Number(v).toFixed(2)}</Text>,
    },
    { title: 'Meses', dataIndex: 'payment_months', width: 60, align: 'center',
      render: v => v || 1,
    },
    { title: 'Valor', dataIndex: 'amount', width: 110, align: 'right',
      render: (v, r) => (
        <Text strong style={{ color: r.kind === 'PAID' ? '#52c41a' : (r.kind === 'RECURRING' ? '#1890ff' : '#faad14') }}>
          R$ {Number(v).toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => a.amount - b.amount,
    },
    { title: 'Mensalidades', dataIndex: 'payments_so_far', width: 110, align: 'center',
      render: v => v ? <Tag color="purple">{v}ª</Tag> : <Tag>—</Tag>,
      sorter: (a, b) => (a.payments_so_far || 0) - (b.payments_so_far || 0),
    },
  ];

  const s = data?.summary || {};

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}><DollarOutlined /> Pagamentos do mês</Title>
          <Text type="secondary">Confirmados + previsão (recorrentes já esperados + trials com uso real)</Text>
        </Col>
        <Col>
          <Space wrap>
            <Segmented
              options={['Todos', 'Pagos', 'Estimados']}
              value={filter}
              onChange={setFilter}
            />
            <DatePicker.MonthPicker value={month} onChange={setMonth} format="MMMM/YYYY" allowClear={false} />
          </Space>
        </Col>
      </Row>

      {/* Cards de resumo */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title={<><CheckCircleOutlined style={{ color: COLORS.PAID }} /> Pagos</>}
              value={s.paid?.amount || 0} prefix="R$" precision={2}
              loading={loading} valueStyle={{ color: COLORS.PAID }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>{s.paid?.count || 0} pagamentos confirmados</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title={<><ClockCircleOutlined style={{ color: COLORS.RECURRING }} /> Recorrentes previstos</>}
              value={s.recurring?.amount || 0} prefix="R$" precision={2}
              loading={loading} valueStyle={{ color: COLORS.RECURRING }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>{s.recurring?.count || 0} times vencem esse mês</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title={<><ExperimentOutlined style={{ color: COLORS.TRIAL }} /> Trials com uso</>}
              value={s.trial?.amount || 0} prefix="R$" precision={2}
              loading={loading} valueStyle={{ color: COLORS.TRIAL }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>{s.trial?.count || 0} trials com grupo+jogo</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card style={{ background: '#f9f9f9' }}>
            <Statistic
              title="Total esperado"
              value={s.total?.amount || 0} prefix="R$" precision={2}
              loading={loading}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>{s.total?.count || 0} times ao todo</Text>
          </Card>
        </Col>
      </Row>

      {/* Gráfico donut */}
      {pieData.length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                     innerRadius={50} outerRadius={90} paddingAngle={2}
                     label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((e) => <Cell key={e.key} fill={COLORS[e.key]} />)}
                </Pie>
                <RTooltip formatter={(v) => `R$ ${Number(v).toFixed(2)}`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Nota sobre metodologia */}
      {(s.trial?.count > 0) && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Estimativa de trials assume ${data?.trial_days_assumed || 30}d de duração e considera apenas times com grupo sincronizado + pelo menos 1 jogo criado (sinal de uso real).`}
        />
      )}

      <Card>
        <Table
          rowKey={r => `${r.kind}-${r.customer_id}-${r.invoice_id || r.subscription_id}`}
          loading={loading}
          dataSource={filteredItems}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} times` }}
          size="small"
          scroll={{ x: 'max-content' }}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell colSpan={6} align="right"><Text strong>Total {filter.toLowerCase()}:</Text></Table.Summary.Cell>
              <Table.Summary.Cell align="right"><Text strong style={{ color: '#52c41a' }}>R$ {filteredTotal.toFixed(2)}</Text></Table.Summary.Cell>
              <Table.Summary.Cell />
            </Table.Summary.Row>
          )}
        />
      </Card>
    </div>
  );
};

export default Payments;
